import { ExperimentProviderContext } from '@carvana/experiment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useIsLoggedIn } from '~/context/common/UserContext';
import type { Experiments } from '~/contracts/common';
import { logError } from '~/utilities/loggers';

export default function useExperimentsData(
  browserCookieIdExperiments: Partial<Experiments>,
  userIdExperiments: Partial<Experiments>,
  precomputedExperiments: Record<string, string | undefined> = {}
) {
  const isLoggedIn = useIsLoggedIn();
  const { experiments } = useContext(ExperimentProviderContext);
  const [experimentsLoaded, setExperimentsLoaded] = useState(false);
  const [forceLoaded, setForceLoaded] = useState(false);

  const experimentNames = useMemo(
    () =>
      Object.keys({
        ...browserCookieIdExperiments,
        ...(isLoggedIn ? userIdExperiments : {})
      }),
    [browserCookieIdExperiments, isLoggedIn, userIdExperiments]
  );

  const contextExperimentsWithBucket = useMemo(
    () =>
      experimentNames.reduce<Record<string, string | null>>(
        (acc, experimentName) => {
          acc[experimentName] =
            experiments[experimentName]?.assignedBucket ?? null;

          return acc;
        },
        {}
      ),
    [experimentNames, experiments]
  );

  // precomputedExperiments are already-resolved (not fetched via
  // ExperimentProviderContext), so they're merged in for reporting but excluded
  // from the loadedExperiments/didLoad tracking below. Normalized to null (not
  // undefined) to match how every other not-yet-bucketed experiment reports,
  // since JSON.stringify would otherwise drop an undefined-valued key entirely.
  const experimentsWithBucket = useMemo(() => {
    const normalizedPrecomputed = Object.fromEntries(
      Object.entries(precomputedExperiments).map(([name, bucket]) => [
        name,
        bucket ?? null
      ])
    );

    return { ...contextExperimentsWithBucket, ...normalizedPrecomputed };
  }, [contextExperimentsWithBucket, precomputedExperiments]);

  const loadedExperiments = useMemo(
    () =>
      Object.entries(contextExperimentsWithBucket)
        .filter(([_, bucket]) => bucket != null)
        .map(([experimentName]) => experimentName),
    [contextExperimentsWithBucket]
  );

  const didLoad =
    loadedExperiments.length === experimentNames.length || forceLoaded;

  useEffect(() => {
    const t = window.setTimeout(() => {
      const unloadedExperiments = Object.entries(contextExperimentsWithBucket)
        .filter(([_, bucket]) => bucket == null)
        .map(([experimentName]) => experimentName);

      logError(new Error('Experiments failed to load'), {
        location: 'Experiments',
        unloadedExperiments
      });
      setForceLoaded(true);
    }, 10000);

    if (didLoad) {
      window.clearTimeout(t);
      setExperimentsLoaded(true);
    }

    return () => window.clearTimeout(t);
  }, [didLoad, contextExperimentsWithBucket, setExperimentsLoaded]);

  return { experimentsWithBucket, experimentNames, experimentsLoaded };
}
